import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { gbp } from "@/lib/stages";
import { ukDayYear } from "@/lib/time";
import { CellMain, CellSub, Empty, KeyValue, Note, PageHead, Panel, RailCard, DeskSupport, Tb, Td, Th } from "@/components/portal/crm/kit";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { FileDown, Loader2 } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const pullStatement = async (year, month) => {
  const res = await api.get(`/refer/statement/${year}/${month}.pdf`, { params: { v: Date.now() }, responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `connexis-commission-${year}-${String(month).padStart(2, "0")}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

const failure = async (e) => {
  if (e?.response?.status === 403) return "Statements belong to an introducer account. Signed in as admin? Pull it from Hub → Introducers.";
  let detail = "";
  try { detail = JSON.parse(await e?.response?.data?.text())?.detail || ""; } catch { /* not JSON */ }
  return detail || "Statement download failed — please try again";
};

const num = { fontVariantNumeric: "tabular-nums" };

/* The statement's "by partner" block, live for the month picked above — the same lines the PDF prints. */
function RecruitLines({ year, month }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    setData(null);
    api.get(`/refer/statement/${year}/${month}/recruits`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ lines: [], recruits: 0, total_gbp: 0 }); });
    return () => { alive = false; };
  }, [year, month]);

  if (data && data.recruits === 0) return null;
  const lines = data?.lines || [];
  return (
    <Panel title={`Your recruited partners · ${MONTHS[month - 1]} ${year}`} pad={false} testId="referrer-statement-recruits"
           sub="Override earned from each call centre, media partner, introducer or desk you brought in — paid by Connexis on top, never out of their commission. These are the lines on the PDF."
           footer={data ? <span data-testid="referrer-statement-recruits-total">Network override this month: <b>{gbp(data.total_gbp)}</b>{data.owed_gbp ? ` · ${gbp(data.owed_gbp)} owed` : ""}{data.paid_gbp ? ` · ${gbp(data.paid_gbp)} paid` : ""}</span> : null}>
      {!data ? (
        <Empty testId="referrer-statement-recruits-loading">Reading the month…</Empty>
      ) : lines.length === 0 ? (
        <Empty testId="referrer-statement-recruits-empty">None of your {data.recruits} recruited partner{data.recruits === 1 ? "" : "s"} funded a deal in {MONTHS[month - 1]} — the override lines appear here the month they do.</Empty>
      ) : (
        <Tb testId="referrer-statement-recruits-table" head={<>
          <Th style={{ width: "44%" }}>Partner</Th>
          <Th style={{ width: "14%" }}>Tier</Th>
          <Th className="r" style={{ width: "12%" }}>Deals</Th>
          <Th className="r" style={{ width: "15%" }}>Owed</Th>
          <Th className="r" style={{ width: "15%" }}>Paid</Th>
        </>}>
          {lines.map((p) => (
            <tr key={p.id} data-testid={`referrer-statement-recruit-${p.id}`}>
              <Td className="lead">
                <CellMain>{p.name}</CellMain>
                <CellSub>{p.label} · {p.override_pct}% override</CellSub>
              </Td>
              <Td><CellMain style={{ fontWeight: 500, color: "var(--mut)" }}>{p.level === 2 ? "Tier 2" : "Tier 1"}</CellMain></Td>
              <Td className="r"><CellMain style={num}>{p.deals}</CellMain></Td>
              <Td className="r"><CellMain style={{ ...num, color: p.owed ? "var(--ink)" : "var(--mut)", fontWeight: p.owed ? 700 : 500 }}>{p.owed ? gbp(p.owed) : "—"}</CellMain></Td>
              <Td className="r"><CellMain style={{ ...num, color: p.paid ? "var(--green)" : "var(--mut)", fontWeight: p.paid ? 700 : 500 }}>{p.paid ? gbp(p.paid) : "—"}</CellMain></Td>
            </tr>
          ))}
        </Tb>
      )}
    </Panel>
  );
}

export default function Statements() {
  const { me, summary } = useOutletContext();
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);

  const years = useMemo(() => [now.getUTCFullYear() - 1, now.getUTCFullYear()], [now]);
  const recent = useMemo(() => {
    const out = [];
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    for (let i = 0; i < 12; i += 1) {
      out.push({ year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 });
      d.setUTCMonth(d.getUTCMonth() - 1);
    }
    return out;
  }, [now]);

  const download = async () => {
    setBusy(true);
    try { await pullStatement(year, month); toast.success(`Statement for ${MONTHS[month - 1]} ${year} downloaded`); }
    catch (e) { toast.error(await failure(e)); }
    finally { setBusy(false); }
  };

  const quick = async (y, m) => {
    setPending(`${y}-${m}`);
    try { await pullStatement(y, m); toast.success(`${MONTHS[m - 1]} ${y} downloaded`); }
    catch (e) { toast.error(await failure(e)); }
    finally { setPending(null); }
  };

  return (
    <>
      <PageHead
        eyebrow="On letterhead, every line shown"
        title="Payout statements"
        lede="Every case that paid you in the month, the VAT position and the bank account it was sent to. Nothing is summarised away."
        testId="referrer-statements-head"
      />

      <div className="grid items-start gap-7 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-7">
          <Panel title="Pull a statement" sub="Pick any month — a statement is generated live from the ledger, not from a file we saved." testId="referrer-statement-picker">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <div className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-500">Year</div>
                <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
                  <SelectTrigger className="h-11 w-28" data-testid="referrer-stmt-year"><SelectValue /></SelectTrigger>
                  <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <div className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-500">Month</div>
                <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
                  <SelectTrigger className="h-11 w-40" data-testid="referrer-stmt-month"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={download} disabled={busy}
                      className="h-11 rounded-[11px] bg-[#0055FF] px-5 text-[13.5px] font-bold hover:bg-[#0044CC]" data-testid="referrer-stmt-download">
                {busy ? <Loader2 className="size-4 animate-spin" /> : <><FileDown className="mr-1.5 size-4" /> Download PDF</>}
              </Button>
            </div>
          </Panel>

          <RecruitLines year={year} month={month} />

          <Panel title="The last twelve months" sub="One tap each. A month with nothing in it comes back as an empty statement rather than an error." pad={false} testId="referrer-statement-archive">
            <div className="grid grid-cols-2 gap-px bg-[#E2E8F0] sm:grid-cols-3">
              {recent.map((r) => (
                <button key={`${r.year}-${r.month}`} onClick={() => quick(r.year, r.month)}
                        className="flex items-center justify-between gap-2 bg-white px-4 py-3.5 text-left transition-colors hover:bg-[#F8FAFF]"
                        data-testid={`statement-${r.year}-${r.month}`}>
                  <span className="font-archivo text-[13.5px] font-bold text-[#0B1220]">{MONTHS[r.month - 1].slice(0, 3)} {r.year}</span>
                  {pending === `${r.year}-${r.month}`
                    ? <Loader2 className="size-3.5 animate-spin text-[#0055FF]" />
                    : <FileDown className="size-3.5 text-slate-400" />}
                </button>
              ))}
            </div>
          </Panel>
        </div>

        <div className="space-y-4">
          <RailCard title="Where it gets paid" testId="referrer-statement-bank">
            <KeyValue label="Account name" value={me?.bank_account_name || "Not set"} />
            <KeyValue label="Sort code" value={me?.bank_sort_code || "—"} />
            <KeyValue label="Account" value={me?.bank_account_masked || "—"} />
            <KeyValue label="VAT" value={me?.vat_number || "Not registered"} />
            <KeyValue label="Last updated" value={ukDayYear(me?.payout_details_updated_at, "—")} />
            <p className="mt-3 border-t border-[#EFF3F8] pt-3 text-[12.5px] leading-relaxed text-slate-500">
              Change these on the Paperwork &amp; setup page. They print on every statement from the moment you save them.
            </p>
          </RailCard>

          <RailCard title="Your year" testId="referrer-statement-year">
            <KeyValue label="Owed" value={gbp(summary?.owed_gbp)} />
            <KeyValue label="Paid" value={gbp(summary?.paid_gbp)} tone="green" />
            <KeyValue label="Funded cases" value={String(summary?.completed ?? 0)} />
          </RailCard>

          <DeskSupport body="A line you don't recognise on a statement? Send us the reference and we'll walk you through it." />
          <Note>Statements are generated on demand, so a figure can move if a desk confirms a fee after month end.</Note>
        </div>
      </div>
    </>
  );
}
